<template>
  <div class="wizard-body columns content-box small-9">
    <form @submit.prevent="submit">
      <div class="table-header">
        <div>
          <h4>{{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.USERS.TITLE') }}</h4>
          <p class="table-subtitle">
            {{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.USERS.DESC') }}
          </p>
        </div>
        <div class="header-actions">
          <span class="selected-count">
            {{ selectedUserIds.length }}
            {{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.USERS.SELECTED_COUNT') }}
          </span>
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            @click.prevent="toggleAll"
          >
            {{
              allSelected
                ? $t('OPPORTUNITY_PIPELINES.ACTIONS.UNSELECT_ALL')
                : $t('OPPORTUNITY_PIPELINES.ACTIONS.SELECT_ALL')
            }}
          </woot-button>
        </div>
      </div>

      <woot-loading-state
        v-if="isFetching"
        :message="$t('OPPORTUNITY_PIPELINES.CREATE_FLOW.USERS.LOADING')"
      />

      <div v-else class="table-wrap">
        <table class="woot-table selection-table">
          <thead>
            <tr>
              <th class="check-col" />
              <th>{{ $t('OPPORTUNITY_PIPELINES.USERS.NAME') }}</th>
              <th>{{ $t('OPPORTUNITY_PIPELINES.USERS.EMAIL') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="agent in agents" :key="agent.id">
              <td class="check-col">
                <input
                  :id="`user-${agent.id}`"
                  v-model="selectedUserIds"
                  class="row-checkbox"
                  type="checkbox"
                  :value="agent.id"
                />
              </td>
              <td>
                <label :for="`user-${agent.id}`" class="row-label">
                  {{ agent.name }}
                </label>
              </td>
              <td>{{ agent.email }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="button-row">
        <woot-button
          type="button"
          color-scheme="secondary"
          @click="goToPrevious"
        >
          {{ $t('OPPORTUNITY_PIPELINES.ACTIONS.BACK') }}
        </woot-button>
        <woot-button :is-loading="isSubmitting">
          {{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.USERS.SUBMIT') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import router from '../../../../index';
import { patchDraft, readDraft } from './draftState';

export default {
  mixins: [alertMixin],
  data() {
    return {
      selectedUserIds: [],
      currentUserIds: [],
      isSubmitting: false,
      isFetching: false,
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
    }),
    pipelineId() {
      return this.$route.params.pipelineId;
    },
    isNewDraftFlow() {
      return this.$route.name === 'settings_opportunity_pipelines_new_users';
    },
    allSelected() {
      return (
        this.agents.length > 0 &&
        this.selectedUserIds.length === this.agents.length
      );
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.isFetching = true;
      try {
        const requests = [this.$store.dispatch('agents/get')];

        if (!this.isNewDraftFlow) {
          requests.push(
            this.$store.dispatch(
              'opportunityPipelines/getUsers',
              this.pipelineId
            )
          );
        }

        const [, selectedUsers] = await Promise.all(requests);

        if (this.isNewDraftFlow) {
          const draft = readDraft();
          this.selectedUserIds = draft.users || [];
        } else {
          this.selectedUserIds = (selectedUsers || []).map(
            item => item.user_id
          );
        }

        this.currentUserIds = [...this.selectedUserIds];
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.FETCH_USERS'));
      } finally {
        this.isFetching = false;
      }
    },
    toggleAll() {
      if (this.allSelected) {
        this.selectedUserIds = [];
        return;
      }
      this.selectedUserIds = this.agents.map(agent => agent.id);
    },
    async submit() {
      if (this.isNewDraftFlow) {
        patchDraft({ users: [...this.selectedUserIds] });
        router.replace({ name: 'settings_opportunity_pipelines_new_inboxes' });
        return;
      }

      this.isSubmitting = true;
      try {
        const toAdd = this.selectedUserIds.filter(
          id => !this.currentUserIds.includes(id)
        );
        const toRemove = this.currentUserIds.filter(
          id => !this.selectedUserIds.includes(id)
        );

        const requests = [];

        if (toAdd.length) {
          requests.push(
            this.$store.dispatch('opportunityPipelines/saveUsers', {
              pipelineId: this.pipelineId,
              userIds: toAdd,
            })
          );
        }

        if (toRemove.length) {
          requests.push(
            this.$store.dispatch('opportunityPipelines/removeUsers', {
              pipelineId: this.pipelineId,
              userIds: toRemove,
            })
          );
        }

        await Promise.all(requests);

        this.currentUserIds = [...this.selectedUserIds];
        router.replace({
          name: 'settings_opportunity_pipelines_inboxes',
          params: { pipelineId: this.pipelineId },
        });
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.SAVE_USERS'));
      } finally {
        this.isSubmitting = false;
      }
    },
    goToPrevious() {
      if (this.isNewDraftFlow) {
        router.replace({ name: 'settings_opportunity_pipelines_new' });
        return;
      }

      router.replace({
        name: 'settings_opportunity_pipelines_edit',
        params: { pipelineId: this.pipelineId },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.table-header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-small);
}

.table-subtitle {
  color: var(--s-700);
  font-size: var(--font-size-small);
  line-height: 1.6;
  margin: 0;
}

.header-actions {
  align-items: center;
  display: flex;
  gap: var(--space-small);
}

.selected-count {
  color: var(--s-700);
  font-size: var(--font-size-small);
}

.table-wrap {
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-medium);
  margin-top: var(--space-small);
  max-height: 48rem;
  overflow: auto;
}

.check-col {
  width: 4rem;
}

.selection-table {
  margin-bottom: 0;
}

.selection-table tbody tr {
  transition: background-color 0.12s ease;
}

.selection-table tbody tr:hover {
  background: var(--s-25);
}

.selection-table td,
.selection-table th {
  line-height: 1.5;
  vertical-align: middle;
}

.row-checkbox {
  accent-color: var(--w-500);
  cursor: pointer;
  margin: 0;
  vertical-align: middle;
}

.row-label {
  cursor: pointer;
  margin: 0;
}

.button-row {
  display: flex;
  gap: var(--space-small);
  justify-content: flex-end;
  margin-top: var(--space-small);
}

@media screen and (max-width: 768px) {
  .table-header {
    flex-direction: column;
    gap: var(--space-small);
  }

  .header-actions {
    justify-content: space-between;
    width: 100%;
  }
}
</style>
